import { useTranslation } from 'react-i18next';

import { Alert, Box, TextField, Typography } from '@mui/material';

import { describeCompositeNode } from '../../lib/publicationRuleCriteria';
import type { CriteriaDescribeContext } from '../../lib/publicationRuleCriteria';
import type { CompositeFilterNode } from '../../model/publicationTypes';

export interface PublicationRuleCompositeCriteriaProps {
  rawJson: string;
  onChangeRawJson: (next: string) => void;
  parsed: CompositeFilterNode | null;
  parseError: string | null;
  describeCtx: CriteriaDescribeContext;
  disabled?: boolean;
}

const PLACEHOLDER = JSON.stringify(
  { op: 'AND', children: [{ categoryIds: ['<category-id>'] }, { productIds: ['<product-id>'] }] },
  null,
  2,
);

/**
 * COMPOSITE_FILTER rule criteria — no visual tree builder (unlike the Promo workspace's condition
 * tree), per this task's scope: a structured read-only summary (best-effort, shape-detecting — see
 * `describeCompositeNode`'s doc comment) plus a raw, validated JSON editor, with a permanent
 * warning banner since this bypasses every other criteria kind's friendlier guardrails.
 */
export function PublicationRuleCompositeCriteria({
  rawJson,
  onChangeRawJson,
  parsed,
  parseError,
  describeCtx,
  disabled,
}: PublicationRuleCompositeCriteriaProps) {
  const { t } = useTranslation('catalog');

  return (
    <Box>
      <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
        {t('publicationRulesPanel.criteriaForm.compositeWarning')}
      </Alert>
      <TextField
        label={t('publicationRulesPanel.criteriaForm.compositeJsonLabel')}
        value={rawJson}
        onChange={(e) => onChangeRawJson(e.target.value)}
        placeholder={PLACEHOLDER}
        disabled={disabled}
        error={parseError !== null}
        helperText={
          parseError !== null
            ? t(`publicationRulesPanel.criteriaForm.compositeJsonErrors.${parseError}`)
            : t('publicationRulesPanel.criteriaForm.compositeJsonHelper')
        }
        fullWidth
        multiline
        minRows={8}
        maxRows={16}
        slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 13 } } }}
      />
      {parsed ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('publicationRulesPanel.criteriaForm.compositeSummaryLabel')}
          </Typography>
          <Typography variant="body2">{describeCompositeNode(describeCtx, parsed, t)}</Typography>
        </Box>
      ) : null}
    </Box>
  );
}
